import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Popover from "@mui/material/Popover"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react"
import { RGBColor } from "react-color"
import type { SimpleStyleT } from "tweenly"
import { parseHexColor, rgbToHex, toRGBColor } from "../../../helpers/color.helpers"
import { endStepRun, markStep } from "../../../helpers/stepUndoGroup.helpers"
import { isStepChange, stepNumber, useWheelStep } from "../../../hooks/useNumberStep"
import { OBJECT_TOOL_CLASSNAME } from "../../Editor/RightSideNavbar"
import ColorPickerInput from "./ColorPickerInput"

interface Props {
    styleProperty: SimpleStyleT
    handleOnChange: (e: { target: { name: string; value: RGBColor } }) => void
}

/** Transparent colors show over a checkerboard, as in every design tool. */
const CHECKERBOARD = "repeating-conic-gradient(#808080 0% 25%, #c0c0c0 0% 50%) 50% / 8px 8px"

/** Fits "100 %" with the field's padding. */
const ALPHA_FIELD_WIDTH = 72

/** Space between the picker and the edge of the panel it opens from. */
const PICKER_GAP_PX = 8

/**
 * A color as one compact row, Figma style: a swatch, the hex and the alpha in %, each labelled
 * inside its field. The full picker (with the eye dropper and recent colors) opens from the
 * swatch instead of taking up the panel. Same props as ColorPickerInput, so the two are
 * interchangeable.
 */
const ColorField = ({ styleProperty, handleOnChange }: Props) => {
    const color = toRGBColor(styleProperty.value)
    const hex = rgbToHex(color)
    const alpha = Math.round((color.a ?? 1) * 100)

    // typed text is kept locally and only committed on blur / Enter
    const [hexText, setHexText] = useState(hex)
    const [alphaText, setAlphaText] = useState(String(alpha))
    useEffect(() => setHexText(hex), [hex])
    useEffect(() => setAlphaText(String(alpha)), [alpha])

    const rowRef = useRef<HTMLDivElement>(null)
    const alphaRef = useRef<HTMLInputElement>(null)
    useWheelStep(alphaRef, (direction) => {
        const next = String(stepNumber(Number(alphaText), direction, 1, 0, 100))
        setAlphaText(next)
        markStep(alphaRef.current)
        commitAlpha(next)
    })
    const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number } | null>(null)

    /**
     * The picker opens beside the panel, over the canvas — the way Figma does it: its right
     * edge just left of the panel, its top level with this row, so the panel stays visible
     * and nothing under the cursor is covered. Outside the tool panel it opens beside the row.
     */
    const openPicker = () => {
        const row = rowRef.current
        if (!row) return
        const rowRect = row.getBoundingClientRect()
        const panel = row.closest(`.${OBJECT_TOOL_CLASSNAME}`)
        const edge = panel ? panel.getBoundingClientRect().left : rowRect.left
        setPickerPosition({ top: rowRect.top, left: edge - PICKER_GAP_PX })
    }

    const commit = (value: RGBColor) =>
        handleOnChange({ target: { name: styleProperty.property, value } })

    const commitHex = () => {
        endStepRun()
        const parsed = parseHexColor(hexText, color.a ?? 1)
        if (parsed && rgbToHex(parsed) !== hex) commit(parsed)
        else setHexText(hex)
    }
    const commitAlpha = (text: string = alphaText) => {
        // an emptied field is not 0 % — Number("") would make the color invisible
        const value = text.trim() === "" ? NaN : Number(text)
        if (!Number.isFinite(value)) return setAlphaText(String(alpha))
        const next = Math.max(0, Math.min(100, value)) / 100
        if (next !== color.a) commit({ ...color, a: next })
        else setAlphaText(String(alpha))
    }
    const onEnter = (commitField: () => void) => (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") commitField()
    }

    const label = styleProperty.input.label ?? styleProperty.property

    return (
        <Box ref={rowRef} display="flex" gap={1} width="100%">
            <Box flex={1} minWidth={0}>
                <TextField
                    value={hexText}
                    onChange={(e) => setHexText(e.target.value)}
                    onBlur={commitHex}
                    onKeyDown={onEnter(commitHex)}
                    type="text"
                    label={label}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{ m: 0 }}
                    inputProps={{ "aria-label": `${label} (hex)`, maxLength: 7 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 1 }}>
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label={`Pick ${label}`}
                                    onClick={openPicker}
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        p: 0,
                                        border: 1,
                                        borderColor: "text.secondary",
                                        borderRadius: "2px",
                                        cursor: "pointer",
                                        background: `linear-gradient(rgba(${color.r}, ${color.g}, ${color.b}, ${color.a}), rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})), ${CHECKERBOARD}`,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            {/* sized to fit "100 %" whatever the panel width; the hex takes the rest */}
            <Box width={ALPHA_FIELD_WIDTH} flexShrink={0}>
                <TextField
                    value={alphaText}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setAlphaText(e.target.value)
                        // stepped (arrows, ↑ / ↓): shown at once, see useNumberStep
                        if (isStepChange(e)) {
                            markStep(e.target)
                            commitAlpha(e.target.value)
                        }
                    }}
                    inputRef={alphaRef}
                    onBlur={() => {
                        endStepRun()
                        commitAlpha()
                    }}
                    onKeyDown={onEnter(() => {
                        endStepRun()
                        commitAlpha()
                    })}
                    type="number"
                    label="alpha"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                        m: 0,
                        "& .MuiFilledInput-root": { pr: 1 },
                        "& .MuiFilledInput-input": { pl: 1, pr: 0 },
                        // no spin arrows in a field this narrow: ↑ / ↓ and the wheel still step
                        "& input[type=number]": { MozAppearance: "textfield" },
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                            WebkitAppearance: "none",
                            m: 0,
                        },
                    }}
                    inputProps={{ "aria-label": `${label} alpha (%)`, min: 0, max: 100 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                sx={{ color: "text.secondary", ml: 0.5, flexShrink: 0 }}
                            >
                                %
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <Popover
                open={Boolean(pickerPosition)}
                anchorReference="anchorPosition"
                anchorPosition={pickerPosition ?? undefined}
                onClose={() => setPickerPosition(null)}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { p: 1, maxWidth: 260 } } }}
            >
                {/* an explicit way out, not only a click somewhere else */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" sx={{ textTransform: "capitalize" }}>
                        {label}
                    </Typography>
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            aria-label="Close"
                            onClick={() => setPickerPosition(null)}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <ColorPickerInput styleProperty={styleProperty} handleOnChange={handleOnChange} />
            </Popover>
        </Box>
    )
}

export default ColorField
